/**
 * "My notifications": the staff notices the club sends to the signed-in
 * person's role, each with a switch to turn it off (or back on) for
 * themselves only. PM 465c33cc: Chris (Gymnastics of York) could not change
 * his own notifications; the only switch was the per-role one on Settings >
 * Manage Notifications, which changes it for everyone on the role and needs
 * the club-settings permission.
 *
 * Saves immediately on change. Shown in the staff profile (StaffPreferencesForm).
 */
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Checkbox, Select, Table, THead, TBody, TR, TH, TD } from "../ui";
import {
  getMyNotifications,
  setMyNotification,
  setMyNoticeDelivery,
  MyNotification,
  MyNotificationsResult,
  NoticeChannel,
  NoticeDelivery,
  NoticeLevel,
} from "../../api/myNotificationPreferencesAPI";

const LEVELS: { level: NoticeLevel; label: string; about: string }[] = [
  { level: "urgent", label: "Urgent", about: "Money, a family waiting, spots running out" },
  { level: "normal", label: "Normal", about: "Things that already happened, like an athlete leaving a class" },
  { level: "low", label: "Low", about: "For your information" },
];
const CHANNELS: { channel: NoticeChannel; label: string }[] = [
  { channel: "bell", label: "Bell" },
  { channel: "email", label: "Email" },
  { channel: "push", label: "Push" },
];
const LEVEL_LABEL: Record<NoticeLevel, string> = { urgent: "Urgent", normal: "Normal", low: "Low" };

// A select value per cell: "" is "as usual" (null), else on / off.
const toValue = (v: boolean | null | undefined) => (v === true ? "on" : v === false ? "off" : "");
const fromValue = (v: string): boolean | null => (v === "on" ? true : v === "off" ? false : null);

function hourLabel(hour: number): string {
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00 ${hour < 12 ? "AM" : "PM"}`;
}

const MyNotificationSettings: React.FC = () => {
  const [items, setItems] = useState<MyNotification[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<NoticeDelivery | null>(null);
  const [digest, setDigest] = useState<{ enabled: boolean; hour: number } | null>(null);
  const [pushAvailable, setPushAvailable] = useState(false);

  const apply = (res: MyNotificationsResult) => {
    if (res.notifications) setItems(res.notifications);
    if (res.delivery) setDelivery(res.delivery);
    if (res.digest) setDigest(res.digest);
    setPushAvailable(res.pushAvailable === true);
  };

  useEffect(() => {
    let live = true;
    getMyNotifications().then((res) => {
      if (!live) return;
      if (res.success && res.notifications) apply(res);
      else setError(res.error || "Could not load your notification settings");
    });
    return () => {
      live = false;
    };
  }, []);

  const toggle = async (key: string, enabled: boolean) => {
    setSaving(key);
    const res = await setMyNotification(key, enabled);
    setSaving(null);
    if (res.success && res.notifications) {
      apply(res);
      toast.success(enabled ? `You will get "${key}" again` : `You will no longer get "${key}"`);
    } else {
      toast.error(res.error || "Could not save your notification setting");
    }
  };

  const choose = async (level: NoticeLevel, channel: NoticeChannel, value: boolean | null) => {
    const key = `${level}:${channel}`;
    setSaving(key);
    const res = await setMyNoticeDelivery(level, channel, value);
    setSaving(null);
    if (res.success && res.notifications) {
      apply(res);
      toast.success("Saved how you get notices");
    } else {
      toast.error(res.error || "Could not save how you get notices");
    }
  };

  const mine = (items || []).filter((n) => n.sent_to_role);

  return (
    <div className="pt-4 border-t border-gray-200 dark:border-gray-700" data-testid="my-notification-settings">
      <label className="block text-sm font-medium mb-1">My notifications</label>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        The staff notices your club sends to your role. Turning one off here stops it for you only, both the
        email and the in-app notice; your colleagues keep getting it. Which roles get each notice is set by
        the club under Settings &gt; Manage Notifications.
      </p>
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      {!error && items === null && <p className="text-sm text-gray-500 dark:text-gray-400">Loading...</p>}
      {!error && items !== null && mine.length === 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">Your club does not send any staff notices to your role.</p>
      )}
      {!error && mine.length > 0 && (
        <div className="space-y-2">
          {mine.map((n) => (
            <Checkbox
              key={n.notification_key}
              label={n.notification_key}
              description={n.level ? LEVEL_LABEL[n.level] : undefined}
              checked={!n.opted_out}
              disabled={saving === n.notification_key}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => toggle(n.notification_key, e.target.checked)}
            />
          ))}
        </div>
      )}

      {!error && delivery && (
        <div className="mt-6" data-testid="my-notice-delivery">
          <label className="block text-sm font-medium mb-1">How you get notices</label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
            For each level, choose the bell (in the app), email and push (the mobile app). &quot;As usual&quot; keeps each
            notice the way it is sent today: the bell or the email it has always used, and no push.{" "}
            {digest?.enabled
              ? `Your club collects normal and low notices into one email a day at ${hourLabel(digest.hour)}; urgent ones always come right away, and push is for urgent notices only.`
              : "Every notice is sent when it happens."}
          </p>
          <Table compact>
            <THead>
              <TR>
                <TH>Level</TH>
                {CHANNELS.map((c) => (
                  <TH key={c.channel}>{c.label}</TH>
                ))}
              </TR>
            </THead>
            <TBody>
              {LEVELS.map(({ level, label, about }) => (
                <TR key={level}>
                  <TD>
                    <div className="font-medium">{label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{about}</div>
                  </TD>
                  {CHANNELS.map(({ channel, label: channelLabel }) => {
                    const isPush = channel === "push";
                    const options = isPush
                      ? [
                          { value: "", label: "Off" },
                          { value: "on", label: "On" },
                        ]
                      : [
                          { value: "", label: "As usual" },
                          { value: "on", label: "On" },
                          { value: "off", label: "Off" },
                        ];
                    const current = delivery[level]?.[channel];
                    return (
                      <TD key={channel}>
                        <Select
                          aria-label={`${label} notices: ${channelLabel}`}
                          value={isPush && current === false ? "" : toValue(current)}
                          options={options}
                          disabled={saving === `${level}:${channel}` || (isPush && !pushAvailable)}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => choose(level, channel, fromValue(e.target.value))}
                        />
                      </TD>
                    );
                  })}
                </TR>
              ))}
            </TBody>
          </Table>
          {!pushAvailable && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Push is not turned on for your club.</p>
          )}
        </div>
      )}
    </div>
  );
};

export default MyNotificationSettings;
